import { createMachine } from 'xstate';
import { type CallEvents, type CallStates } from './call.types';

/**
 * The state machine only defines the possible states and transitions. The actual business logic is
 * executed in the context of the components using hooks.
 *
 * @see https://xstate.js.org/docs/
 */

export const callStateMachine = createMachine<unknown, CallEvents, CallStates>({
  /** @xstate-layout N4IgpgJg5mDOIC5QGMCGAbdBiAygFQEEAlPAbQAYBdRUABwHtYBLAFyfoDsaQAPRAWgAsARgB0AZnIBWAOwAOOQE4ATIKnKAbHKkAaEAE9Ew8YNFS5wjYvFTFijYMHjlAXxd60mUZ-QBJDqxMGEwAXkwcUFi+AHK+eAD6ACIA8tEAohTUSCAMzGyc3HwIyuTkosIy0vIqFTLKynqGxXKmUpbkdsrOmpIabh4Y6N6D-oHBYRFRsQkAYgS+ADJpiZncuYEF2UUlZRVVSsq19Y2IFhL1KnaKpYeKcv0gPsOYRHAsqABObJMxcUmpGSoa0YGy4WyMzg0og0NkEmmkggcchOCFkYmUMg05Dh10EMkE5Fc7keg2e6AA6qhAhEZvQPgAFT5sZBMWioDgsWBYenEPC+ADCvh50QSAClkjFlqtsut8mDQEVKuJRPjBBYpE5ZHjxCjlG0zBphCU5Ic5JVhFIHk8fJTqVBaQymUwWWyOVyeSQBUKCCKcPECAsiGkCIkAJrxGLSugguWFRBKlWOdWa-EyHUGRCSMTiOQaOpK+SHLpW0k2qnfe10xlfZ2s9mcrDRZLxD18wXCvB+8WSlZAmUx9jy3jx8jK1XJmyp9NNEpiNNSLTWOzmYQmkteMt2h3V5l1t0EABmLDAH1esHeX25vK9Hfi3fSvay0byg7jCAT44tKe1KIJYlHMiYoimLkFiGiWsS1qDLaFbbk6Lr1rAh7HqebxMlenrtj6nb+oGwZhhG0RRjkA6bAqI5jkmX6Tj+GYIGoZS5s4cgmIobRyNi65DJusFVvBe6cshJ5nheLCNs2rY3thXYSg+xGyq+4LvqOiZqtRWppiiNjokaqjmAuigEooMhcWS-j0h89BQB8cBcgAEj6ADi8QAKr0vJpFDkURrmOUwjGcZgF5lIC5aRoygqrYHTyJihnKLmpk+OZlnWbZWCOUQyRufE-IBgsLbXlhIrxEGACyyQAGpSn2z6gm+LQqmxVjkBaMJ1MYKL8FCGLSOQdThVijjKIoiUjBwFlWTZsDuoV3rFUsMxkDVJEvmRw4IMILWmJYMh3PUhziPY06ZoiEgcXqq52CxHSjZgyWTWlklFWKsnVU+K11Upm3YtCu3YvU53XBour2GYGpppIHEyMIRriLdfjjSlU1ckGIbhngzZLAQVUeatXlGHqch+QFgGYjIIXA3RPTlOQHFYl0ZqjoI8P3al01YEG+C8oRcS+AQeBvcCeNvj5RMwyTQXk6FdEiBF5iIpYVhgXqzMPBw9AQHA3A+ELn3kQg-BKBI+wHGomjaJ11hmNYBKOOFfXiMILMBGw4zhFAuuxkpeK6vIoiaIoRqbRxHTOCZkGloMIlOhEnuKfrwjAaI0UaDCgjWInhkoguyriOFh0cdo1w5vDMHu3BNYIW6cdrUUssGnTcJOFYuIg2I1hdDnxniEBpfluXfGVwJSFHsJaFfDX+MICYY4sUxFgmnnMjZ4aEjGf5VgYuFLHOxNbPwP2wtfUzogtKuHRb5Iii-i1-tVJU0NKFIoEQQMG6DAsYCoAAbu7k8i0nFOacM7p0EFpGGp8NQmkOGmOED94afx-u7ISHw0gfEsh8f+Sk7DKgcNDDU6hZBtV1KUaEC4FAYk2iFFoRI3BAA */
  id: 'call',
  predictableActionArguments: true,
  initial: 'idle',
  states: {
    idle: {
      on: {
        START: 'callInitializing',
        RESUME: 'callResuming',
        START_TEST_CALL: 'testCallInitializing',
        START_PHONE_CALL: 'phoneCallInitializing',
      },
    },
    phoneCallInitializing: {
      on: {
        INIT_DONE: 'phoneCallRinging',
        INIT_FAILED: 'phoneCallLeavingAfterError',
        HANG_UP: 'phoneCallLeaving.providerCanceledRinging',
      },
    },
    phoneCallLeavingAfterError: {
      on: {
        START: 'callInitializing',
        START_TEST_CALL: 'testCallInitializing',
        START_PHONE_CALL: 'phoneCallInitializing',
        RESUME: 'callResuming',
      },
    },
    phoneCallRinging: {
      on: {
        HANG_UP: 'phoneCallLeaving.providerCanceledRinging',
        PHONE_CALL_PATIENT_UNANSWERED: 'phoneCallLeaving.patientUnanswered',
        PHONE_CALL_PATIENT_DECLINED: 'phoneCallLeaving.patientDeclined',
        PHONE_CALL_PATIENT_FAILED: 'phoneCallLeavingAfterError',
        PHONE_PARTICIPANT_ANSWERED: 'phoneCallInProgress',
      },
    },
    phoneCallInProgress: {
      on: {
        HANG_UP: 'phoneCallLeaving.providerCanceled',
        PHONE_CALL_PATIENT_DECLINED: 'phoneCallLeaving.patientDeclined',
        PHONE_CALL_PATIENT_FAILED: 'phoneCallLeavingAfterError',
        PHONE_CALL_PATIENT_LEFT: 'phoneCallLeaving.patientCanceled',
      },
    },
    phoneCallLeaving: {
      on: {
        START: 'callInitializing',
        START_TEST_CALL: 'testCallInitializing',
        START_PHONE_CALL: 'phoneCallInitializing',
        RESUME: 'callResuming',
      },
      states: {
        providerCanceled: {},
        providerCanceledRinging: {},
        patientUnanswered: {},
        patientDeclined: {},
        patientCanceled: {},
      },
    },
    testCallInitializing: {
      after: {
        30_000: {
          target: 'testCallLeavingAfterError',
        },
      },
      on: {
        PARTICIPANT_JOINED: 'testCallInProgress',
      },
    },
    testCallLeaving: {
      on: {
        START_TEST_CALL: 'testCallInitializing',
        START: 'callInitializing',
      },
    },
    testCallLeavingAfterError: {
      on: {
        START_TEST_CALL: 'testCallInitializing',
        START: 'callInitializing',
      },
    },
    testCallInProgress: {
      on: {
        HANG_UP: 'testCallLeaving',
      },
    },
    callInitializing: {
      after: {
        30_000: 'callLeavingAfterError.timeout',
      },
      on: {
        INIT_DONE: 'callWaitingForParticipants',
        INIT_FAILED: 'callLeavingAfterError.error',
      },
    },
    callResuming: {
      after: {
        30_000: 'callLeavingAfterError.timeout',
      },
      on: {
        INIT_DONE: 'callWaitingForParticipantsAfterResume',
        INIT_FAILED: 'callLeavingAfterError.error',
      },
    },
    callRestarting: {
      on: {
        INIT_DONE: 'callWaitingForParticipantsAfterRestart',
      },
    },
    callWaitingForParticipants: {
      after: {
        30_000: 'callLeavingAfterError.timeout',
      },
      on: {
        PARTICIPANT_JOINED: 'callInProgress',
        PARTICIPANTS_ALREADY_IN: 'callInProgress',
      },
    },
    callWaitingForParticipantsAfterRestart: {
      on: {
        PARTICIPANT_JOINED: 'callInProgress',
        PARTICIPANTS_ALREADY_IN: 'callInProgress',
      },
    },
    callWaitingForParticipantsAfterResume: {
      after: {
        30_000: 'callLeavingAfterError.timeout',
      },
      on: {
        PARTICIPANT_JOINED: 'callInProgress',
        PARTICIPANTS_ALREADY_IN: 'callInProgress',
      },
    },
    callInProgress: {
      initial: 'running',
      states: {
        running: {
          on: {
            RECONNECTING: '#call.callInProgress.reconnecting',
          },
        },
        reconnecting: {
          on: {
            RECONNECTED: '#call.callInProgress.running',
          },
        },
      },
      on: {
        HANG_UP: 'callLeaving.hangUp',
        HOLD: 'callOnHold',
        GROUP_CALL_PARTICIPANT_REMOVED:
          'callLeaving.groupCallParticipantRemoved',
        PROVIDER_LEFT: 'callLeaving.providerLeft',
        LAST_PATIENT_LEFT: 'callLeaving.lastPatientLeft',
        RESTART_INITIATED: 'callRestarting',
        TRANSFER_TO_NEW_ROOM_INITIATED: 'callLeavingAfterTransferToNewRoom',
        PATIENT_LEFT: {},
        REDIRECTING_TO_DASHBOARD: 'redirectingToDashboard',
      },
    },
    callOnHold: {
      on: {
        RESUME: 'callResuming',
        HANG_UP: 'callLeaving.hangUp',
        HANG_UP_ON_HOLD: 'callLeaving.hangUpOnHold',
      },
    },

    callLeaving: {
      on: {
        START: 'callInitializing',
        START_TEST_CALL: 'testCallInitializing',
        START_PHONE_CALL: 'phoneCallInitializing',
        RESUME: 'callResuming',
      },
      states: {
        groupCallParticipantRemoved: {},
        providerLeft: {},
        lastPatientLeft: {},
        hangUp: {},
        hangUpOnHold: {},
      },
    },
    callLeavingAfterTransferToNewRoom: {
      on: {
        START: 'callInitializing',
        START_TEST_CALL: 'testCallInitializing',
        START_PHONE_CALL: 'phoneCallInitializing',
        RESUME: 'callResuming',
      },
    },
    callLeavingAfterError: {
      on: {
        START: 'callInitializing',
        START_TEST_CALL: 'testCallInitializing',
        START_PHONE_CALL: 'phoneCallInitializing',
        RESUME: 'callResuming',
      },
      states: {
        timeout: {},
        error: {},
      },
    },
    redirectingToDashboard: {
      on: {
        START: 'callInitializing',
        RESUME: 'callResuming',
        START_TEST_CALL: 'testCallInitializing',
        START_PHONE_CALL: 'phoneCallInitializing',
      },
    },
  },
});
